'use client';

import { useState, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';

type FormInputs = {
  email: string;
};

export const NewsletterPopup = () => {
  const [showPopup, setShowPopup] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<{
    message?: string;
    error?: string;
  }>({});
  const faxRef = useRef<HTMLInputElement>(null);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormInputs>({ mode: 'onChange' });

  useEffect(() => {
    const lastShown = localStorage.getItem('newsletterPopup');
    const now = new Date().getTime();
    const twoDays = 2 * 24 * 60 * 60 * 1000; // 2 days in milliseconds

    // If popup was never shown OR 2 days have passed since last time
    if (!lastShown || now - parseInt(lastShown) > twoDays) {
      const handleScroll = () => {
        const scrollPercentage =
          (window.scrollY /
            (document.documentElement.scrollHeight - window.innerHeight)) *
          100;

        if (scrollPercentage > 50) {
          setShowPopup(true);
          localStorage.setItem('newsletterPopup', now.toString()); // Save timestamp
          window.removeEventListener('scroll', handleScroll);
        }
      };

      window.addEventListener('scroll', handleScroll);
      return () => window.removeEventListener('scroll', handleScroll);
    }
  }, []);

  const closePopup = () => {
    setShowPopup(false);
    localStorage.setItem('newsletterPopup', new Date().getTime().toString()); // Prevents showing again for 2 days
  };

  const onSubmit = async (data: FormInputs) => {
    setIsLoading(true);
    try {
      const formData = new FormData();
      formData.append('email', data.email);
      formData.append('fax', faxRef.current?.value || '');

      const response = await fetch('/api/newsletter', {
        method: 'POST',
        body: formData,
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to subscribe');
      }

      setSubmitStatus({ message: result.message });
      setTimeout(closePopup, 8000);
    } catch (error) {
      setSubmitStatus({
        error: error instanceof Error ? error.message : 'Failed to subscribe',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    showPopup && (
      <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/50 backdrop-blur-sm">
        <div className="relative max-w-[32rem] overflow-hidden rounded-2xl bg-white shadow-2xl">
          {/* Decorative Elements */}
          <div className="pointer-events-none absolute inset-0">
            <div className="absolute -top-20 -right-20 h-40 w-40 rounded-full bg-indigo-100/50 blur-3xl"></div>
            <div className="absolute -bottom-20 -left-20 h-40 w-40 rounded-full bg-purple-200/50 blur-3xl"></div>
          </div>

          <button
            className="absolute top-4 right-4 z-10 flex h-8 w-8 cursor-pointer items-center justify-center rounded-full bg-gray-100 text-gray-500 transition-all hover:bg-gray-200 hover:text-gray-700"
            onClick={closePopup}
          >
            ✖
          </button>

          <div className="relative p-10">
            <div className="mb-2 inline-flex rounded-full bg-indigo-100 px-3 py-1 text-xs font-medium text-indigo-700">
              Newsletter
            </div>

            <h3 className="font-serif text-2xl text-gray-900">
              Join Our Mindful Community
            </h3>

            <p className="mt-3 text-gray-600">
              Get weekly inspiration, practical tips, and exclusive mindfulness
              resources delivered straight to your inbox.
            </p>

            <form onSubmit={handleSubmit(onSubmit)} className="mt-6 space-y-4">
              <div className="group relative">
                <input
                  {...register('email', {
                    required: 'Email is required',
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: 'Invalid email address',
                    },
                  })}
                  type="email"
                  placeholder="Enter your email"
                  className="w-full rounded-lg border-2 border-gray-200 bg-white px-4 py-3 text-gray-600 transition-all placeholder:text-gray-400 focus:border-indigo-300 focus:ring-2 focus:ring-indigo-500/20 focus:outline-none"
                />

                {errors.email && (
                  <span className="mt-1 text-sm text-rose-600">
                    {errors.email.message}
                  </span>
                )}
                {submitStatus.error && (
                  <span className="mt-1 text-sm text-rose-600">
                    {submitStatus.error}
                  </span>
                )}
                {submitStatus.message && (
                  <span className="mt-1 text-sm text-indigo-600">
                    {submitStatus.message}
                  </span>
                )}
                <button
                  type="submit"
                  disabled={isLoading}
                  className="mt-3 w-full cursor-pointer rounded-lg bg-gradient-to-r from-indigo-500 to-purple-500 px-6 py-3 text-sm font-medium text-white transition-all duration-300 hover:translate-y-[-2px] hover:shadow-lg hover:shadow-indigo-500/25 disabled:cursor-not-allowed disabled:opacity-70"
                >
                  {isLoading ? (
                    <div className="flex items-center justify-center">
                      <svg
                        className="mr-2 h-5 w-5 animate-spin"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                          fill="none"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                      </svg>
                      Subscribing...
                    </div>
                  ) : (
                    'Subscribe Now'
                  )}
                </button>
              </div>

              <div
                aria-hidden="true"
                style={{
                  position: 'absolute',
                  left: '-9999px',
                  top: '-9999px',
                }}
              >
                <label htmlFor="fax">FAX</label>
                <input
                  id="fax"
                  type="text"
                  name="fax"
                  ref={faxRef}
                  tabIndex={-1}
                  autoComplete="off"
                />
              </div>
            </form>
          </div>
        </div>
      </div>
    )
  );
};
